/**
 * Reads and writes for sample scanning, through the host app's own providers so a move here is the same
 * move as anywhere else in the app (LocationProvider also keeps the sample's storage location current).
 *
 * A host can pass its own store with the same methods instead (tests do).
 */

/**
 * @typedef {Object} SampleScanStore
 * @property {() => Promise<Array<Object>>} listLocations - The customer's locations.
 * @property {(pIDLocation: number) => Promise<Object|null>} getLocation
 * @property {(pIDSample: number) => Promise<Array<{ IDLocation: number }>|null>} getSampleLocations - Where the sample is now, newest first; null when it can't be read.
 * @property {(pIDSample: number, pIDLocation: number) => Promise<any>} addSampleToLocation
 * @property {(pIDSample: number, pIDLocation: number) => Promise<any>} removeSampleFromLocation
 * @property {(pIDSample: number, pStatus: string) => Promise<void>} setSampleStatus - Writes only the status.
 * @property {(pLog: Object) => Promise<any>} createSampleLog
 * @property {() => Promise<{ CaptureLatitude?: number, CaptureLongitude?: number }|null>} getPosition - Never prompts.
 * @property {() => Promise<Object|null>} readReceiveConfig - `QRCodes` / `Receive`; null when it can't be read.
 */

class AppSampleScanStore
{
	/**
	 * @param {any} pPict
	 */
	constructor(pPict)
	{
		this.pict = pPict;
	}

	/**
	 * @param {string} pURL
	 * @return {Promise<any>} the body, or null on error
	 */
	getJSON(pURL)
	{
		return new Promise((fResolve) =>
		{
			this.pict.HeadlightRestClient.getJSON(pURL, (pError, pBody) => fResolve(pError ? null : pBody));
		});
	}

	async listLocations()
	{
		const tmpCached = this.pict.AppData && this.pict.AppData.Locations;
		if (Array.isArray(tmpCached) && tmpCached.length)
		{
			return tmpCached;
		}
		return this.pict.providers.LocationProvider.getAllLocations().catch(() => []);
	}

	getLocation(pIDLocation)
	{
		return this.pict.providers.LocationProvider.getLocationByID(Number(pIDLocation));
	}

	/**
	 * The sample's location joins, newest first; null when they can't be read.
	 * @param {number} pIDSample
	 * @return {Promise<Array<{ IDSampleLocationJoin: number, IDLocation: number }>|null>}
	 */
	async getSampleLocations(pIDSample)
	{
		const tmpBody = await this.getJSON(`SampleLocationJoins/FilteredTo/FBV~IDSample~EQ~${Number(pIDSample)}~FSF~CreateDate~DESC~0`);
		return Array.isArray(tmpBody) ? tmpBody : null;
	}

	addSampleToLocation(pIDSample, pIDLocation)
	{
		return this.pict.providers.LocationProvider.addSampleToLocation(pIDSample, pIDLocation);
	}

	removeSampleFromLocation(pIDSample, pIDLocation)
	{
		return this.pict.providers.LocationProvider.removeSamplefromLocation(pIDSample, pIDLocation);
	}

	setSampleStatus(pIDSample, pStatus)
	{
		return new Promise((fResolve, fReject) =>
		{
			this.pict.HeadlightRestClient.putJSON('Sample', { IDSample: pIDSample, Status: pStatus }, (pError) =>
			{
				if (pError)
				{
					return fReject(pError);
				}
				fResolve();
			});
		});
	}

	createSampleLog(pLog)
	{
		return this.pict.providers.SampleLogProvider.createSampleLog(pLog);
	}

	async getPosition()
	{
		const tmpGeo = this.pict.providers.GeoProvider;
		if (!tmpGeo)
		{
			return null;
		}
		return tmpGeo.getGeoCoords({ NoPrompt: true }).catch(() => null);
	}

	readReceiveConfig()
	{
		return this.getJSON('Configuration/For/QRCodes/Receive');
	}
}

module.exports = AppSampleScanStore;
